import { useCallback } from 'react';
import { useProjectStore } from '../store/projectStore';

export function Inspector() {
    const { nodes, edges, onNodesChange } = useProjectStore();

    // Find selected node or edge
    const selectedNode = nodes.find((n) => (n.selected as any) === true);
    const selectedEdge = edges.find((e) => (e.selected as any) === true);

    const handleNodeDataChange = useCallback(
        (field: string, value: unknown) => {
            if (!selectedNode) return;

            onNodesChange([
                {
                    type: 'replace',
                    id: selectedNode.id,
                    item: {
                        ...selectedNode,
                        data: {
                            ...selectedNode.data,
                            [field]: value,
                        },
                    },
                },
            ]);
        },
        [selectedNode, onNodesChange],
    );

    const handleNodePropertyChange = useCallback(
        (field: 'display_name' | 'color' | 'x' | 'y', value: unknown) => {
            if (!selectedNode) return;

            if (field === 'x' || field === 'y') {
                onNodesChange([
                    {
                        type: 'position',
                        id: selectedNode.id,
                        position: {
                            x: field === 'x' ? (value as number) : selectedNode.position.x,
                            y: field === 'y' ? (value as number) : selectedNode.position.y,
                        },
                    },
                ]);
            } else {
                // For display_name and color, store in data or style
                const updatedNode = {
                    ...selectedNode,
                };
                if (field === 'color') {
                    updatedNode.style = { ...(updatedNode.style as any), backgroundColor: value };
                } else if (field === 'display_name') {
                    updatedNode.data = { ...updatedNode.data, display_name: value };
                }

                onNodesChange([
                    {
                        type: 'replace',
                        id: selectedNode.id,
                        item: updatedNode,
                    },
                ]);
            }
        },
        [selectedNode, onNodesChange],
    );

    if (!selectedNode && !selectedEdge) {
        return (
            <div className="p-3 text-muted small">
                <p>Select a node or edge to edit properties.</p>
            </div>
        );
    }

    if (selectedNode) {
        return (
            <div className="p-3 border-top">
                <h6 className="mb-3">Node Properties</h6>

                <div className="mb-3">
                    <label className="form-label small">Type</label>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        value={selectedNode.type || ''}
                        disabled
                    />
                </div>

                <div className="mb-3">
                    <label className="form-label small">Display Name</label>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        value={(selectedNode.data?.display_name as string) || ''}
                        onChange={(e) => handleNodePropertyChange('display_name', e.target.value)}
                    />
                </div>

                <div className="mb-3">
                    <label className="form-label small">Position</label>
                    <div className="d-flex gap-2">
                        <div className="flex-grow-1">
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                placeholder="X"
                                value={Math.round(selectedNode.position.x)}
                                onChange={(e) => handleNodePropertyChange('x', parseInt(e.target.value))}
                            />
                        </div>
                        <div className="flex-grow-1">
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                placeholder="Y"
                                value={Math.round(selectedNode.position.y)}
                                onChange={(e) => handleNodePropertyChange('y', parseInt(e.target.value))}
                            />
                        </div>
                    </div>
                </div>

                <div className="mb-3">
                    <label className="form-label small">Color</label>
                    <input
                        type="color"
                        className="form-control form-control-sm"
                        value={(selectedNode.style as any)?.backgroundColor || '#ffffff'}
                        onChange={(e) => handleNodePropertyChange('color', e.target.value)}
                    />
                </div>

                {selectedNode.type === 'fragment' && (
                    <div className="mb-3">
                        <label className="form-label small">Text</label>
                        <textarea
                            className="form-control form-control-sm"
                            rows={4}
                            value={(selectedNode.data?.text as string) || ''}
                            onChange={(e) => handleNodeDataChange('text', e.target.value)}
                        />
                    </div>
                )}

                {selectedNode.type === 'comment' && (
                    <div className="mb-3">
                        <label className="form-label small">Comment</label>
                        <textarea
                            className="form-control form-control-sm"
                            rows={3}
                            value={(selectedNode.data?.text as string) || ''}
                            onChange={(e) => handleNodeDataChange('text', e.target.value)}
                        />
                    </div>
                )}

                {(selectedNode.type === 'hub' || selectedNode.type === 'group') && (
                    <div className="mb-3">
                        <label className="form-label small">Label</label>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            value={(selectedNode.data?.label as string) || ''}
                            onChange={(e) => handleNodeDataChange('label', e.target.value)}
                        />
                    </div>
                )}
            </div>
        );
    }

    if (selectedEdge) {
        return (
            <div className="p-3 border-top">
                <h6 className="mb-3">Connection Properties</h6>
                <p className="small text-muted">Connection editing coming in Phase 5.</p>
            </div>
        );
    }
}
